import { Badge, Button, Modal } from '@/components/ui';

export interface AgentLoginResult {
  vicidial_user: string;
  state: string;
  detail: string;
}

/**
 * How one agent's login check is described, in one place.
 *
 * The Vicidial servers page and the Vicidial agents page both ask the same
 * question through the same endpoint. Rendering the answer twice invited them
 * to drift, and "can this agent log in" is not a question that should have two
 * different-looking answers.
 */
export function agentStateLabel(state: string): { label: string; tone: 'green' | 'amber' | 'gray' | 'red' } {
  switch (state) {
    case 'OK':
      return { label: 'Can log in', tone: 'green' };
    case 'NO_PASSWORD':
      return { label: 'No password', tone: 'amber' };
    case 'UNKNOWN':
      return { label: 'Unchecked', tone: 'gray' };
    default:
      return { label: 'Rejected', tone: 'red' };
  }
}

export function AgentLoginList({ agents }: { agents: AgentLoginResult[] }) {
  return (
    <ul className="max-h-72 divide-y divide-line overflow-y-auto rounded-lg border border-line">
      {agents.map((agent) => {
        const state = agentStateLabel(agent.state);
        return (
          <li
            key={agent.vicidial_user}
            className="flex items-start justify-between gap-3 px-3 py-2"
          >
            <span className="font-mono text-xs text-ink-800 dark:text-ink-100">
              {agent.vicidial_user}
            </span>
            <span className="flex items-center gap-2">
              <span className="text-right text-2xs text-ink-500">{agent.detail}</span>
              <Badge tone={state.tone}>{state.label}</Badge>
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/** The results of a Test agents run, shown on the agents page. */
export function AgentLoginResultsModal({
  result,
  onClose,
}: {
  result: { agents: AgentLoginResult[]; agents_total: number; agents_ok: number; errors: string[] };
  onClose: () => void;
}) {
  const failing = result.agents_total - result.agents_ok;
  return (
    <Modal
      open
      wide
      onClose={onClose}
      title="Test agents"
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className="space-y-4">
        <div
          className={
            failing === 0 && result.agents_total > 0
              ? 'rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/50 dark:text-emerald-200'
              : 'rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/50 dark:text-amber-200'
          }
        >
          <strong className="font-semibold">
            {result.agents_ok} of {result.agents_total} agent(s) can log in.
          </strong>
          {failing > 0 && (
            <span className="block">
              An agent that cannot log in produces silence on a call, not an
              error — so it is worth fixing before going live.
            </span>
          )}
        </div>

        {result.errors.length > 0 && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 dark:border-red-900 dark:bg-red-950/50 dark:text-red-300">
            {result.errors.join('; ')}
          </div>
        )}

        {result.agents_total === 0 ? (
          <p className="text-sm text-ink-600 dark:text-ink-300">
            No enabled agents to test. Add them with Bulk add agents, including
            their Vicidial passwords.
          </p>
        ) : (
          <AgentLoginList agents={result.agents} />
        )}
      </div>
    </Modal>
  );
}
